import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { saveFile, loadFiles, clearFiles } from "@/lib/formDraft";
import api, { brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2, Loader2, ShieldCheck, ArrowRight, Camera, FileText, Store, Car, Briefcase } from "lucide-react";
import { CameraCapture, isInAppBrowser } from "@/components/CameraCapture";
import { DocSlot } from "@/components/DocSlot";
import { ContatosFamiliares, pendenciasContatos, maskTel, telValido, VAZIO } from "@/components/ContatosFamiliares";
import { EnderecoCep, enderecoFaltante } from "@/components/EnderecoCep";
import { erroIdentificacao, cpfValido, nascimentoValido } from "@/lib/refresh";
import { toast } from "sonner";
import logoDark from "@/assets/logo-dark.png";

const BASE_DOCS = [
  { key: "rg_frente", tipo: "RG ou CNH (frente)", icon: FileText, required: true, modalidade: "foto" },
  { key: "rg_verso", tipo: "RG ou CNH (verso)", icon: FileText, required: true, modalidade: "foto" },
  { key: "selfie", tipo: "Selfie segurando documento", icon: Camera, required: true, modalidade: "foto" },
  { key: "comprovante_residencia", tipo: "Comprovante de residência", icon: FileText, required: true },
  { key: "foto_casa", tipo: "Foto da frente da casa", icon: Camera, required: true, modalidade: "foto" },
  { key: "cnh_digital", tipo: "CNH digital (PDF)", icon: FileText, modalidade: "digital" },
];
const COMERCIANTE_DOCS = [
  { key: "comprovante_comercial", tipo: "Comprovante comercial", icon: FileText, required: true },
  { key: "foto_comercio", tipo: "Foto do comércio", icon: Camera, required: true, modalidade: "foto" },
];
const CLT_DOCS = [
  { key: "carteira_trabalho", tipo: "Carteira de trabalho (registro/vínculo)", icon: FileText, required: true },
];
const MOTORISTA_DOCS = [
  { key: "foto_carro", tipo: "Foto do carro", icon: Camera, required: true, modalidade: "foto" },
  { key: "conta_99pop", tipo: "Conta / print 99POP", icon: Car },
  { key: "conta_uber", tipo: "Conta / print Uber", icon: Car },
  { key: "crlv_digital", tipo: "CRLV digital (PDF)", icon: Car, modalidade: "digital" },
  { key: "crlv_foto", tipo: "Documento do veículo (foto)", icon: Car, modalidade: "foto" },
];

export default function PublicApplication() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const draftNs = `pub-draft:${slug || "default"}`;
  const [empresa, setEmpresa] = useState(null);
  const params = new URLSearchParams(window.location.search);
  const ref = params.get("ref") || "";
  useEffect(() => {
    if (slug) { api.get(`/public/empresas/${slug}`).then((r) => setEmpresa(r.data)).catch(() => setEmpresa({ erro: true })); return; }
    if (ref) { api.get(`/public/links/${ref}`).then((r) => navigate(`/solicitar-credito/${r.data.slug}?ref=${ref}`, { replace: true })).catch(() => setEmpresa({ erro: true })); return; }
    setEmpresa({ erro: true });
  }, [slug, ref, navigate]);
  const brandName = empresa?.nome || (slug === "trt" ? "TRT CRÉDITO RÁPIDO" : "");
  const logoSrc = empresa?.logo ? `${process.env.REACT_APP_BACKEND_URL}/api/public/empresas/${slug}/logo` : (slug === "trt" ? logoDark : null);
  const [valor, setValor] = useState(500);
  const [tipoAtividade, setTipoAtividade] = useState("");
  const [files, setFiles] = useState({});
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lgpd, setLgpd] = useState(false);
  const [erroId, setErroId] = useState("");
  const [f, setF] = useState({
    nome: "", cpf: "", data_nascimento: "", telefone: "", telefone_contato: "",
    cnpj: "", nome_comercio: "", veiculo: "", placa: "", cnh: "",
    subtipo_atividade: "", profissao: "", possui_garantia: null, garantia_descricao: "",
    contatos_familiares: [VAZIO(), VAZIO()],
  });
  const [mostrarErros, setMostrarErros] = useState(false);
  const [end, setEnd] = useState({ cep: "", rua: "", numero: "", sem_numero: false, complemento: "", bairro: "", cidade: "", estado: "" });
  const [prog, setProg] = useState({});
  const [aidPend, setAidPend] = useState(null); // solicitação já criada (reenvio após falha não duplica)

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setFile = (key, file) => { setFiles((p) => ({ ...p, [key]: file })); saveFile(draftNs, key, file); };
  const [restored, setRestored] = useState(false);
  const [cam, setCam] = useState(null);
  const inApp = isInAppBrowser();
  const copyLink = async () => { try { await navigator.clipboard.writeText(window.location.href); toast.success("Link copiado"); } catch { toast.error("Não foi possível copiar"); } };

  // Rascunho: restaura tudo se o navegador recarregar a página (ex.: ao abrir a câmera no celular)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftNs);
      if (raw) {
        const d = JSON.parse(raw);
        if (d.f) setF((p) => ({ ...p, ...d.f }));
        if (d.end) setEnd((p) => ({ ...p, ...d.end }));
        if (d.aid) setAidPend(d.aid);
        if (d.valor) setValor(d.valor);
        if (d.tipoAtividade) setTipoAtividade(d.tipoAtividade);
        if (d.lgpd) setLgpd(true);
      }
    } catch { /* ignorado */ }
    loadFiles(draftNs).then((fl) => {
      if (Object.keys(fl).length) { setFiles((p) => ({ ...fl, ...p })); toast.success("Recuperamos suas fotos e dados preenchidos."); }
      setRestored(true);
    });
  }, [draftNs]);
  useEffect(() => {
    if (!restored) return;
    const hasData = Object.values(f).some(Boolean) || tipoAtividade || lgpd;
    if (hasData) localStorage.setItem(draftNs, JSON.stringify({ f, end, aid: aidPend, valor, tipoAtividade, lgpd }));
  }, [f, end, aidPend, valor, tipoAtividade, lgpd, restored, draftNs]);

  const docSlots = [
    ...BASE_DOCS,
    ...(tipoAtividade === "comerciante" ? COMERCIANTE_DOCS : []),
    ...(tipoAtividade === "motorista" ? MOTORISTA_DOCS : []),
    ...(tipoAtividade === "outros" && f.subtipo_atividade === "clt" ? CLT_DOCS : []),
  ];
  const pendContatos = pendenciasContatos(f.contatos_familiares, f.telefone, true);
  const pendPerfil = [
    ...(!tipoAtividade ? ["Selecione o perfil de atividade"] : []),
    ...(tipoAtividade === "outros" && !f.subtipo_atividade ? ["Informe se é CLT ou Outro"] : []),
    ...(tipoAtividade === "outros" && f.subtipo_atividade === "outro" && !f.profissao.trim() ? ["Informe sua profissão/atividade"] : []),
    ...(f.possui_garantia === null ? ["Informe se possui garantia"] : []),
    ...(f.possui_garantia === true && f.garantia_descricao.trim().length < 3 ? ["Descreva o bem oferecido como garantia"] : []),
    ...(!telValido(f.telefone) ? ["Telefone principal com DDD"] : []),
  ];

  const submit = async (e) => {
    e.preventDefault();
    if (!f.nome || !f.telefone) {
      toast.error("Preencha ao menos nome e telefone.");
      return;
    }
    if (pendContatos.length || pendPerfil.length) { setMostrarErros(true); toast.error(`Campos pendentes: ${[...pendPerfil, ...pendContatos].join("; ")}`); return; }
    const eId = erroIdentificacao(f.cpf, f.data_nascimento);
    if (eId) { setErroId(eId); toast.error(eId); return; }
    setErroId("");
    if (!lgpd) {
      toast.error("É necessário aceitar o termo de consentimento LGPD.");
      return;
    }
    const missing = docSlots.filter((s) => s.required && !files[s.key]);
    if (tipoAtividade === "motorista" && !files.conta_99pop && !files.conta_uber) {
      missing.push({ tipo: "Conta / print 99POP ou Uber" });
    }
    if (missing.length) {
      toast.error(`Envie os documentos obrigatórios: ${missing.map((s) => s.tipo).join(", ")}`);
      return;
    }
    if ((end.cep || "").replace(/\D/g, "").length !== 8) { toast.error("Informe o CEP do seu endereço."); return; }
    if (enderecoFaltante(end).length && !end.manual) { toast.error("Confirme o endereço pelo CEP ou preencha os campos manualmente."); return; }
    setLoading(true);
    try {
      let aid = aidPend, msgOk = "";
      if (!aid) {
        const { data } = await api.post(`/public/applications${slug ? `?empresa=${slug}` : ""}`, {
          ...f, ...end, estado: end.estado, valor_desejado: valor, tipo_atividade: tipoAtividade, aceite_lgpd: true,
          possui_comercio: tipoAtividade === "comerciante", link_token: ref,
        });
        aid = data.application_id; setAidPend(aid); msgOk = data.message;
      }
      let failed = 0;
      for (const s of docSlots.filter((x) => files[x.key] && prog[x.key]?.st !== "ok")) {
        const fd = new FormData();
        fd.append("tipo", s.tipo);
        fd.append("modalidade", s.modalidade || "");
        fd.append("file", files[s.key]);
        setProg((p) => ({ ...p, [s.key]: { st: "enviando", pct: 0 } }));
        try {
          await api.post(`/public/applications/${aid}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" }, onUploadProgress: (ev) => setProg((p) => ({ ...p, [s.key]: { st: "enviando", pct: ev.total ? Math.round((ev.loaded * 100) / ev.total) : 0 } })) });
          setProg((p) => ({ ...p, [s.key]: { st: "ok", pct: 100 } }));
        } catch (e) { failed++; setProg((p) => ({ ...p, [s.key]: { st: "erro", msg: e.response?.data?.detail || "" } })); }
      }
      if (failed > 0) { toast.error(`${failed} documento(s) não foram enviados. Seus dados foram preservados — substitua o arquivo ou toque em Enviar novamente.`); setLoading(false); return; }
      try { await api.post(`/public/applications/${aid}/concluir`); }
      catch (e) { toast.error(e.response?.data?.detail || "Documentos obrigatórios pendentes. Reenvie os documentos faltantes."); setLoading(false); return; }
      setSent(msgOk || "Solicitação enviada com sucesso. Nossa equipe realizará a análise e entrará em contato.");
      localStorage.removeItem(draftNs);
      clearFiles(draftNs);
      window.scrollTo(0, 0);
    } catch {
      toast.error("Erro ao enviar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  if (sent)
    return (
      <div className="min-h-screen bg-onyx flex items-center justify-center p-6">
        <div className="max-w-md text-center fade-up">
          <div className="w-20 h-20 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-400" />
          </div>
          <h1 className="text-white text-2xl font-bold tracking-tight" data-testid="public-success-title">Solicitação Enviada!</h1>
          <p className="text-slate-400 mt-3">{sent}</p>
          {logoSrc ? <img src={logoSrc} alt={brandName} className="h-16 mx-auto mt-8 opacity-90 object-contain" /> : <div className="text-white font-bold mt-8">{brandName}</div>}
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-loan-application-form">
      <header className="bg-onyx py-3 px-6 flex items-center justify-center sticky top-0 z-10" data-testid="public-header">
        {logoSrc ? <img src={logoSrc} alt={brandName} className="h-12 max-w-[200px] object-contain" /> : <span className="text-white font-extrabold text-lg tracking-tight" data-testid="public-brand-name">{brandName}</span>}
      </header>
      {empresa?.erro && <div className="bg-red-50 text-red-700 text-sm text-center py-3 px-4 font-semibold" data-testid="public-empresa-erro">Link inválido: empresa não identificada. Solicite um novo link à empresa que fará sua análise de crédito.</div>}

      <div className="max-w-3xl mx-auto p-4 md:p-8">
        <div className="text-center mb-8 fade-up">
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Solicite seu crédito online</h1>
          <p className="text-slate-500 mt-2">Informe o valor desejado, seus dados e envie seus documentos.</p>
        </div>

        {/* Value */}
        <div className="trt-card p-6 mb-6 gold-border">
          <div className="flex justify-between mb-2">
            <Label className="text-base font-semibold">Valor solicitado</Label>
            <span className="font-extrabold gold-text mono text-xl" data-testid="sim-valor">{brl(valor)}</span>
          </div>
          <Slider value={[valor]} min={100} max={1000} step={50} onValueChange={(v) => setValor(v[0])} data-testid="sim-valor-slider" />
          <div className="flex justify-between text-xs text-slate-400 mt-1"><span>R$ 100</span><span>R$ 1.000</span></div>
          <p className="text-xs text-slate-400 mt-3">O valor final será definido pela nossa equipe após a análise.</p>
        </div>

        <form onSubmit={submit} className="space-y-6">
          <section className="trt-card p-6">
            <h3 className="font-bold text-lg mb-4">Dados pessoais</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Nome completo *"><Input data-testid="pub-nome" value={f.nome} onChange={(e) => set("nome", e.target.value)} /></Field>
              <Field label="CPF *"><Input data-testid="pub-cpf" required inputMode="numeric" value={f.cpf} onChange={(e) => set("cpf", e.target.value)} placeholder="000.000.000-00" />{erroId && !cpfValido(f.cpf) && <p className="text-xs text-red-600 mt-1" data-testid="pub-cpf-erro">Informe um CPF válido (11 dígitos).</p>}</Field>
              <Field label="Data de nascimento *"><Input type="date" required data-testid="pub-nascimento" max={new Date().toISOString().slice(0, 10)} value={f.data_nascimento} onChange={(e) => set("data_nascimento", e.target.value)} />{erroId && !nascimentoValido(f.data_nascimento) && <p className="text-xs text-red-600 mt-1" data-testid="pub-nascimento-erro">Informe uma data de nascimento válida (não futura).</p>}</Field>
              <Field label="Telefone *"><Input data-testid="pub-telefone" inputMode="numeric" value={f.telefone} onChange={(e) => set("telefone", maskTel(e.target.value))} placeholder="(00) 00000-0000" />{mostrarErros && !telValido(f.telefone) && <p className="text-xs text-red-600 mt-1">Informe DDD + número.</p>}</Field>
            </div>
            <div className="mt-4"><h4 className="font-semibold text-sm mb-2">Contatos familiares <span className="text-red-500">*</span></h4><p className="text-xs text-slate-500 mb-2">Dois contatos de familiares, diferentes do seu telefone.</p><ContatosFamiliares value={f.contatos_familiares} onChange={(v) => set("contatos_familiares", v)} telefonePrincipal={f.telefone} testid="pub-contato" mostrarErros={mostrarErros} /></div>
            <div className="mt-4"><h4 className="font-semibold text-sm mb-2">Endereço</h4><EnderecoCep e={end} onChange={setEnd} testid="pub" /></div>
          </section>

          <section className="trt-card p-6">
            <h3 className="font-bold text-lg mb-1">Atividade</h3>
            <p className="text-slate-500 text-sm mb-4">Você é motorista de aplicativo, comerciante ou outro (CLT etc.)? A seleção não representa aprovação automática.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="pub-atividades">
              <button type="button" onClick={() => setTipoAtividade("motorista")} data-testid="pub-atividade-motorista"
                className={`flex items-center gap-3 rounded-xl border-2 p-4 min-h-[64px] w-full min-w-0 text-left transition-all ${tipoAtividade === "motorista" ? "border-gold bg-amber-50" : "border-slate-200 hover:border-gold"}`}>
                <Car className={`w-6 h-6 shrink-0 ${tipoAtividade === "motorista" ? "gold-text" : "text-slate-400"}`} />
                <span className="font-semibold text-base sm:text-sm text-left min-w-0 break-words">Motorista de aplicativo<br /><span className="text-xs text-slate-400 font-normal">Uber / 99</span></span>
              </button>
              <button type="button" onClick={() => setTipoAtividade("comerciante")} data-testid="pub-atividade-comerciante"
                className={`flex items-center gap-3 rounded-xl border-2 p-4 min-h-[64px] w-full min-w-0 text-left transition-all ${tipoAtividade === "comerciante" ? "border-gold bg-amber-50" : "border-slate-200 hover:border-gold"}`}>
                <Store className={`w-6 h-6 shrink-0 ${tipoAtividade === "comerciante" ? "gold-text" : "text-slate-400"}`} />
                <span className="font-semibold text-base sm:text-sm text-left min-w-0 break-words">Comerciante<br /><span className="text-xs text-slate-400 font-normal">Loja / comércio</span></span>
              </button>
              <button type="button" onClick={() => setTipoAtividade("outros")} data-testid="pub-atividade-outros"
                className={`flex items-center gap-3 rounded-xl border-2 p-4 min-h-[64px] w-full min-w-0 text-left transition-all ${tipoAtividade === "outros" ? "border-gold bg-amber-50" : "border-slate-200 hover:border-gold"}`}>
                <Briefcase className={`w-6 h-6 shrink-0 ${tipoAtividade === "outros" ? "gold-text" : "text-slate-400"}`} />
                <span className="font-semibold text-base sm:text-sm text-left min-w-0 break-words">Outros / CLT<br /><span className="text-xs text-slate-400 font-normal">Carteira assinada ou outra atividade</span></span>
              </button>
            </div>
            {mostrarErros && !tipoAtividade && <p className="text-xs text-red-600 mt-2">Selecione o perfil.</p>}
            {tipoAtividade === "outros" && (
              <div className="mt-4 space-y-3" data-testid="pub-outros-fields">
                <div className="grid grid-cols-2 gap-2">
                  {[["clt", "CLT (carteira assinada)"], ["outro", "Outro"]].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => set("subtipo_atividade", v)} data-testid={`pub-subtipo-${v}`} className={`rounded-lg border px-3 py-2 text-sm font-semibold ${f.subtipo_atividade === v ? "bg-onyx text-white border-onyx" : "bg-white hover:border-gold"}`}>{l}</button>
                  ))}
                </div>
                {f.subtipo_atividade === "outro" && <Field label="Profissão / atividade *"><Input data-testid="pub-profissao" value={f.profissao} onChange={(e) => set("profissao", e.target.value)} /></Field>}
                {f.subtipo_atividade === "clt" && <Field label="Profissão / cargo"><Input data-testid="pub-profissao" value={f.profissao} onChange={(e) => set("profissao", e.target.value)} /></Field>}
              </div>
            )}
            <div className="mt-5 rounded-xl border p-4" data-testid="pub-garantia">
              <p className="font-semibold text-sm">Possui garantia? <span className="text-red-500">*</span></p>
              <p className="text-xs text-slate-500 mb-2">Informar um bem não cria termo nem substitui o fluxo de garantia e assinatura da empresa.</p>
              <div className="grid grid-cols-2 gap-2">
                {[[true, "Sim"], [false, "Não"]].map(([v, l]) => (
                  <button key={l} type="button" onClick={() => set("possui_garantia", v)} data-testid={`pub-garantia-${l.toLowerCase()}`} className={`rounded-lg border px-3 py-2 text-sm font-semibold ${f.possui_garantia === v ? "bg-onyx text-white border-onyx" : "bg-white hover:border-gold"}`}>{l}</button>
                ))}
              </div>
              {f.possui_garantia === true && <div className="mt-3"><Field label="Descreva o bem oferecido *"><Input data-testid="pub-garantia-descricao" value={f.garantia_descricao} onChange={(e) => set("garantia_descricao", e.target.value)} placeholder="Ex.: moto Honda CG 160 2020, placa..." /></Field></div>}
              {mostrarErros && f.possui_garantia === null && <p className="text-xs text-red-600 mt-2">Informe Sim ou Não.</p>}
            </div>
            {tipoAtividade === "motorista" && (
              <div className="grid md:grid-cols-3 gap-4 mt-4" data-testid="pub-motorista-fields">
                <Field label="CNH (nº)"><Input data-testid="pub-cnh" value={f.cnh} onChange={(e) => set("cnh", e.target.value)} /></Field>
                <Field label="Veículo (marca/modelo/ano)"><Input data-testid="pub-veiculo" value={f.veiculo} onChange={(e) => set("veiculo", e.target.value)} /></Field>
                <Field label="Placa"><Input data-testid="pub-placa" value={f.placa} onChange={(e) => set("placa", e.target.value.toUpperCase())} /></Field>
              </div>
            )}
            {tipoAtividade === "comerciante" && (
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <Field label="Nome do comércio"><Input data-testid="pub-nome-comercio" value={f.nome_comercio} onChange={(e) => set("nome_comercio", e.target.value)} /></Field>
                <Field label="CNPJ"><Input data-testid="pub-cnpj" value={f.cnpj} onChange={(e) => set("cnpj", e.target.value)} placeholder="00.000.000/0000-00" /></Field>
              </div>
            )}
          </section>

          <section className="trt-card p-6">
            <h3 className="font-bold text-lg mb-1">Documentos</h3>
            <p className="text-slate-500 text-sm mb-4">Envie fotos legíveis. A selfie deve mostrar você segurando o documento. Itens marcados com <span className="text-red-500 font-bold">*</span> são obrigatórios.</p>
            {inApp && (
              <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 space-y-2" data-testid="inapp-browser-warning">
                <p><b>Câmera bloqueada neste navegador interno.</b> Para tirar fotos, abra este link no Chrome ou Safari (menu ⋮ → &ldquo;Abrir no navegador&rdquo;), ou use a opção Galeria.</p>
                <button type="button" onClick={copyLink} className="underline font-semibold" data-testid="copy-link-btn">Copiar link</button>
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              {docSlots.map((s) => (
                <DocSlot key={s.key} slot={s} file={files[s.key]} progresso={prog[s.key]} onFile={(file) => { setFile(s.key, file); setProg((p) => ({ ...p, [s.key]: undefined })); }} onOpenCamera={setCam} />
              ))}
            </div>
          </section>
          <CameraCapture open={!!cam} facing={cam?.facing} title={cam?.slot?.tipo} onClose={() => setCam(null)}
            onCapture={(file) => cam && setFile(cam.slot.key, file)} onFallback={cam?.fallback} />

          <label className="flex items-start gap-3 rounded-xl border border-slate-300 bg-white p-4 cursor-pointer" data-testid="lgpd-consent">
            <Checkbox checked={lgpd} onCheckedChange={(v) => setLgpd(!!v)} data-testid="lgpd-checkbox"
              className="mt-0.5 border-slate-500 data-[state=checked]:bg-gold data-[state=checked]:border-gold data-[state=checked]:text-black" />
            <span className="text-sm text-slate-800 leading-relaxed">
              Autorizo a <b className="text-onyx" data-testid="lgpd-empresa">{brandName || "a empresa responsável por este link"}</b> a coletar e tratar meus dados pessoais e documentos enviados
              exclusivamente para análise de crédito, formalização de contrato e cobrança, conforme a Lei Geral de Proteção de Dados
              (Lei nº 13.709/2018). Posso solicitar a exclusão dos meus dados a qualquer momento.
            </span>
          </label>

          {(pendContatos.length > 0 || pendPerfil.length > 0 || docSlots.some((s) => s.required && !files[s.key])) && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="pub-pendencias">
              <b>Pendências para enviar:</b> {[...pendPerfil, ...pendContatos, ...docSlots.filter((s) => s.required && !files[s.key]).map((s) => `Documento: ${s.tipo}`)].join("; ")}
            </div>
          )}
          <Button type="submit" disabled={loading || !lgpd || docSlots.some((s) => s.required && !files[s.key]) || (tipoAtividade === "motorista" && !files.conta_99pop && !files.conta_uber)} data-testid="pub-submit-btn"
            className="w-full bg-gold hover:bg-[hsl(var(--gold-hover))] text-black h-12 font-bold text-base gap-2">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Enviar solicitação <ArrowRight className="w-5 h-5" /></>}
          </Button>
          <div className="flex items-center justify-center gap-2 text-slate-400 text-xs">
            <ShieldCheck className="w-4 h-4" /> Seus dados e documentos são protegidos conforme a LGPD.
          </div>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><Label className="text-sm">{label}</Label><div className="mt-1.5">{children}</div></div>
);
